import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiUrl } from "@/utils/api";

/**
 * "Request a demo" form for the UDZM landing page.
 *
 * Layout, field order and labels follow the reference demo-request form; its
 * multiple-choice questions (current software, yearly jumps, topics) are open
 * text fields so the prospect answers in their own words. Email is the
 * recommended contact method — it is required, and the contact-method field
 * says so.
 *
 * Posts with raw axios + apiUrl(), never the app's axios instance: that one
 * carries a JWT interceptor whose 401 handling redirects to /login, which an
 * anonymous visitor must never hit. `noValidate` on the form so every browser
 * (Firefox's native bubbles differ from Chrome's) shows the same inline
 * messages, in the page's language.
 */

const COPY = {
  en: {
    title: "Request a Live Demo",
    intro: "Tell us about your dropzone and we will get back to you to schedule your 1.5-hour live demo with our team.",
    name: "Full name",
    namePh: "Jane Cooper",
    dropzone: "Dropzone / Company name",
    dropzonePh: "Skydive Example",
    email: "Work email",
    emailPh: "you@yourdropzone.com",
    phone: "Phone (optional)",
    phonePh: "+1 555 000 0000",
    preferredContact: "Preferred contact method",
    preferredContactPh: "Email (recommended)",
    preferredContactHint: "We recommend email: it is the quickest way for us to schedule your demo.",
    location: "Where is your dropzone located?",
    locationPh: "City, Country — helps us schedule across time zones",
    currentSoftware: "What do you currently use to manage your dropzone?",
    currentSoftwarePh: "Software, spreadsheets, or anything else you use today",
    annualJumps: "Approximate jumps per year",
    annualJumpsPh: "Your best estimate",
    topics: "What would you like to discuss?",
    topicsPh: "Tell us what you would like to cover",
    availability: "Best days / times for your demo",
    availabilityPh: "e.g. Weekdays after 2 PM EST",
    message: "Anything else we should know?",
    messagePh: "Fleet size, number of instructors, specific needs…",
    submit: "Request my demo",
    submitting: "Sending…",
    privacy: "We will only use this information to arrange your demo. No spam, no sharing with third parties.",
    required: "required",
    errRequired: "Please fill in this field.",
    errEmail: "Please enter a valid email address, e.g. you@yourdropzone.com.",
    errFix: "Please fix the highlighted fields.",
    errNetwork: "We couldn't send your request. Check your connection and try again.",
    errServer: "Something went wrong on our side. Please try again in a moment.",
    successTitle: "Thank you!",
    successBody: "Your request has been sent. We will email you shortly to schedule your 1.5-hour live demo.",
    close: "Close",
  },
  fr: {
    title: "Demander une démo en direct",
    intro: "Parlez-nous de votre dropzone et nous vous recontacterons pour planifier votre démo en direct de 1 h 30 avec notre équipe.",
    name: "Nom complet",
    namePh: "Jeanne Tremblay",
    dropzone: "Nom de la dropzone / de l'entreprise",
    dropzonePh: "Parachutisme Exemple",
    email: "Courriel professionnel",
    emailPh: "vous@votredropzone.com",
    phone: "Téléphone (facultatif)",
    phonePh: "+1 555 000 0000",
    preferredContact: "Moyen de contact préféré",
    preferredContactPh: "Courriel (recommandé)",
    preferredContactHint: "Nous recommandons le courriel : c'est le moyen le plus rapide de planifier votre démo.",
    location: "Où se trouve votre dropzone?",
    locationPh: "Ville, pays — pour planifier selon votre fuseau horaire",
    currentSoftware: "Qu'utilisez-vous actuellement pour gérer votre dropzone?",
    currentSoftwarePh: "Logiciel, tableurs ou tout autre outil utilisé aujourd'hui",
    annualJumps: "Nombre approximatif de sauts par année",
    annualJumpsPh: "Votre meilleure estimation",
    topics: "De quoi aimeriez-vous discuter?",
    topicsPh: "Dites-nous ce que vous aimeriez aborder",
    availability: "Meilleurs jours / heures pour votre démo",
    availabilityPh: "ex. En semaine après 14 h (HNE)",
    message: "Autre chose que nous devrions savoir?",
    messagePh: "Taille de la flotte, nombre d'instructeurs, besoins particuliers…",
    submit: "Demander ma démo",
    submitting: "Envoi…",
    privacy: "Nous utiliserons ces renseignements uniquement pour organiser votre démo. Aucun pourriel, aucun partage avec des tiers.",
    required: "obligatoire",
    errRequired: "Veuillez remplir ce champ.",
    errEmail: "Veuillez entrer une adresse courriel valide, ex. vous@votredropzone.com.",
    errFix: "Veuillez corriger les champs indiqués.",
    errNetwork: "Nous n'avons pas pu envoyer votre demande. Vérifiez votre connexion et réessayez.",
    errServer: "Une erreur s'est produite de notre côté. Veuillez réessayer dans un instant.",
    successTitle: "Merci!",
    successBody: "Votre demande a été envoyée. Nous vous écrirons sous peu pour planifier votre démo en direct de 1 h 30.",
    close: "Fermer",
  },
};

const EMPTY = {
  name: "",
  dropzone: "",
  email: "",
  phone: "",
  preferredContact: "",
  location: "",
  currentSoftware: "",
  annualJumps: "",
  topics: "",
  availability: "",
  message: "",
  website: "", // honeypot — hidden from people
};

const REQUIRED = ["name", "dropzone", "email", "location"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateDemoRequest = (values) => {
  const errors = {};
  for (const key of REQUIRED) {
    if (!String(values[key] || "").trim()) errors[key] = "required";
  }
  if (!errors.email && !EMAIL_RE.test(String(values.email).trim())) errors.email = "email";
  return errors;
};

const inputClass = (invalid) =>
  `w-full px-2.5 py-2.5 border rounded-md text-[15px] bg-white text-[#1a1a1a] placeholder:text-[#9a9a9a] focus:outline-none focus:ring-2 focus:ring-red-600/40 ${
    invalid ? "border-red-600" : "border-[#ccc] focus:border-red-600"
  }`;

const Field = ({ id, label, required, requiredLabel, hint, error, children }) => (
  <div className="mb-4">
    <label htmlFor={id} className="block font-bold mb-1.5 text-sm">
      {label}
      {required && (
        <>
          {" "}
          <span className="text-red-600" aria-hidden="true">*</span>
          <span className="sr-only">({requiredLabel})</span>
        </>
      )}
    </label>
    {children}
    {hint && !error && (
      <p id={`${id}-hint`} className="text-xs text-[#555] mt-1.5">{hint}</p>
    )}
    {error && (
      <p id={`${id}-error`} className="text-xs text-red-600 mt-1.5" role="alert">{error}</p>
    )}
  </div>
);

const DemoRequestDialog = ({ open, onOpenChange, lang = "en" }) => {
  const c = COPY[lang] || COPY.en;
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [sent, setSent] = useState(false);

  const handleOpenChange = (next) => {
    // A sent form starts fresh next time; an unsent draft is kept so closing
    // by accident doesn't lose what was typed.
    if (!next && sent) {
      setValues(EMPTY);
      setErrors({});
      setSent(false);
    }
    if (!next) setSubmitError("");
    onOpenChange(next);
  };

  const set = (key) => (e) => {
    const v = e.target.value;
    setValues((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const messageFor = (key) => {
    if (errors[key] === "required") return c.errRequired;
    if (errors[key] === "email") return c.errEmail;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError("");

    const found = validateDemoRequest(values);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setSubmitError(c.errFix);
      const first = REQUIRED.find((k) => found[k]);
      if (first) document.getElementById(`demo-${first}`)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, String(v).trim()])
      );
      await axios.post(apiUrl("/demo-requests"), { ...payload, language: lang === "fr" ? "fr" : "en" });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (!err?.response) setSubmitError(c.errNetwork);
      else if (status === 429) setSubmitError(err.response.data?.message || c.errServer);
      else if (status === 400) setSubmitError(c.errFix);
      else setSubmitError(c.errServer);
    } finally {
      setSubmitting(false);
    }
  };

  const describedBy = (id, hasHint = false) =>
    errors[id.replace("demo-", "")] ? `${id}-error` : hasHint ? `${id}-hint` : undefined;

  const text = (key, { required = false, type = "text", autoComplete, hint } = {}) => {
    const id = `demo-${key}`;
    return (
      <Field
        id={id}
        label={c[key]}
        required={required}
        requiredLabel={c.required}
        hint={hint}
        error={messageFor(key)}
      >
        <input
          id={id}
          name={key}
          type={type}
          value={values[key]}
          onChange={set(key)}
          placeholder={c[`${key}Ph`]}
          autoComplete={autoComplete}
          aria-required={required || undefined}
          aria-invalid={errors[key] ? true : undefined}
          aria-describedby={describedBy(id, Boolean(hint))}
          className={inputClass(Boolean(errors[key]))}
        />
      </Field>
    );
  };

  const area = (key) => {
    const id = `demo-${key}`;
    return (
      <Field id={id} label={c[key]}>
        <textarea
          id={id}
          name={key}
          value={values[key]}
          onChange={set(key)}
          placeholder={c[`${key}Ph`]}
          className={`${inputClass(false)} min-h-[90px] resize-y`}
        />
      </Field>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="font-['DM_Sans'] bg-white text-[#1a1a1a] max-w-[600px] max-h-[90vh] overflow-y-auto block p-6 sm:p-8"
      >
        {sent ? (
          <div className="py-6 text-center" role="status">
            <DialogTitle className="text-2xl font-bold mb-3">{c.successTitle}</DialogTitle>
            <DialogDescription className="text-[#555] text-base mb-6">{c.successBody}</DialogDescription>
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-semibold"
            >
              {c.close}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="max-w-[560px] mx-auto">
            <DialogTitle className="text-2xl font-bold mb-1.5 pr-8">{c.title}</DialogTitle>
            <DialogDescription className="text-[#555] text-sm mb-5">{c.intro}</DialogDescription>

            {/* Honeypot: off-screen and skipped by keyboard + assistive tech. */}
            <div className="absolute -left-[10000px] w-px h-px overflow-hidden" aria-hidden="true">
              <label htmlFor="demo-website">Website</label>
              <input
                id="demo-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={values.website}
                onChange={set("website")}
              />
            </div>

            {text("name", { required: true, autoComplete: "name" })}
            {text("dropzone", { required: true, autoComplete: "organization" })}
            {text("email", { required: true, type: "email", autoComplete: "email" })}
            {text("phone", { type: "tel", autoComplete: "tel" })}
            {text("preferredContact", { hint: c.preferredContactHint })}
            {text("location", { required: true, autoComplete: "address-level2" })}
            {text("currentSoftware")}
            {text("annualJumps")}
            {text("topics")}
            {text("availability")}
            {area("message")}

            {submitError && (
              <div className="mb-4 border border-red-600 bg-red-50 text-red-700 text-sm rounded-md px-3 py-2" role="alert">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded-md text-base font-semibold"
            >
              {submitting ? c.submitting : c.submit}
            </button>
            <p className="text-xs text-[#777] mt-3.5">{c.privacy}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoRequestDialog;
